import React, { useState, useEffect } from 'react';
import { OptionButton } from '@parchment/forms';
import { IndividualIcon, ThirdPartyIcon } from '@parchment/icons';
import { Anchor, Banner, Divider, FormContainer, Heading4,
    Heading5 as Heading5Raw, mercury, ShowMoreContainer, wildSand } from '@parchment/atoms';
import moment from 'moment';
import styled from 'styled-components';
import LandingForm from './LandingForm';
import ThirdPartyConfirmationPage from './ThirdPartyConfirmationPage';
import RegistrationView from './RegistrationView';
import LoginView from './LoginView';
import BaseLayout from './BaseLayout';
import { ORDER_TYPE } from '../common/constants/OrderType';
import useEmailAvailability from '../common/hooks/EmailAvailabilityHooks';
import MemberMessages from './MemberMessages';
import {MessageContext, MessagePageLocation, MessageUserRole} from '../common/constants/MessageConstants';

export default function LandingPage({
    member = {},
    ssoInfo = {},
    registrationTokenRecord = {},
    submitting = false,
    orderType = (location.hash === '#/tpo' ? ORDER_TYPE.THIRD_PARTY : ORDER_TYPE.LEARNER),
    emailVerified = false,
    memberServiceMessages = {}
}) {
    const [ landingPageState, setLandingPageState ] =  useState({
        showOfficeClosedBanner: true,
        orderType: orderType,
        isSubmittingForm: submitting,
        showErrorBanner: false,
        errorBannerMessage: '',
        email: ssoInfo?.email || '',
        emailVerified: emailVerified
    });
    const { isSubmittingForm: formSubmitting } = landingPageState;
    const { emailAvailability, checkEmailInUse, status: emailAvailableStatus } = useEmailAvailability();
    useEffect(() => {
        if (formSubmitting) {
            checkEmailInUse(landingPageState.email);
        }
    }, [formSubmitting]);
    useEffect(() => {
        if (formSubmitting && emailAvailability.emailCheckPerformed) {
            setLandingPageState((prevState) => ({
                ...prevState,
                isSubmittingForm: false,
                emailVerified: true
            }));
        }
    }, [emailAvailability]);

    useEffect(() => {
        if (formSubmitting && emailAvailableStatus?.hasError) {
            setLandingPageState((prevState) => ({
                ...prevState,
                isSubmittingForm: false,
                showErrorBanner: true,
                errorBannerMessage: emailAvailableStatus.errorMessage
            }));
        }
    }, [emailAvailableStatus]);

    useEffect(() => {
        if(registrationTokenRecord?.studentEmail) {
            setLandingPageState((prevState) => ({
                ...prevState,
                email: registrationTokenRecord.studentEmail
            }));
        }
    }, [registrationTokenRecord]);

    const isOfficeClosed = (closeStartTime, closeEndTime) => {
        if (closeStartTime !== null && closeEndTime !== null) {
            const startTime = moment(closeStartTime, 'X').utc();
            const endTime = moment(closeEndTime, 'X').utc();
            const currTime = moment().utc().valueOf();
            return (startTime.isSameOrBefore(currTime)) &&
                (endTime.isSameOrAfter(currTime));
        }

        return false;
    };
    const formatMessage = (message) => {
        return message.split(/[\r\n]+/g).map((item, key) => {
            return <MessageDiv key={key}>{item}</MessageDiv>;
        });
    };

    const resetEmail = (newOrderType = ORDER_TYPE.LEARNER) => {
        let resetEmailState = {
            email: null,
            emailVerified: false,
            orderType: newOrderType
        };

        setLandingPageState((prevState) => ({
            ...prevState,
            ...resetEmailState
        }));
    };

    const displayFormTitle = () => {
        const { available: emailAvailable } = emailAvailability;
        const { orderType, emailVerified } = landingPageState;

        let formTitle = '';
        const type = (orderType === ORDER_TYPE.LEARNER) ? 'Learner' : 'Third Party';
        if (emailVerified && emailAvailable) {
            formTitle = 'New ' + type + ' Account';
        } else if (emailVerified && !emailAvailable) {
            formTitle = type + ' Sign In';
        } else {
            formTitle = type + ' Account';
        }

        return formTitle;
    };

    const getPageLocation = () => {
        const { emailVerified } = landingPageState;
        if (emailVerified) {
            return MessagePageLocation.REGISTRATION_REGISTER;
        } else {
            return MessagePageLocation.REGISTRATION_SIGN_IN;
        }
    };

    const renderPageBasedMessages = (context) => {
        return (
            <MemberMessages
                memberServiceMessages={memberServiceMessages}
                pageLocation={getPageLocation()}
                userRole={(landingPageState.orderType === ORDER_TYPE.LEARNER) ? MessageUserRole.LEARNER : MessageUserRole.THIRD_PARTY}
                messageContexts={context}
            />
        );
    };

    const renderWelcomeMessage = () => {
        const { welcomeMessage, name: memberName } = member;
        return (
            <WelcomeMessageContainer>
                <div id='welcome-message-container'>
                    <Heading5>A message from {memberName}</Heading5>
                    { welcomeMessage &&
                        <StyledContainer
                            collapsedHeight={300}>
                            { formatMessage(welcomeMessage) }
                        </StyledContainer>
                    }
                </div>
                {messagesAvailable && renderPageBasedMessages([MessageContext.SIMPLE])}
            </WelcomeMessageContainer>
        );
    };
    const messagesAvailable = memberServiceMessages && (memberServiceMessages?.globalMessages?.length > 0 || memberServiceMessages?.associatedMemberMessages?.length > 0 || memberServiceMessages?.memberMessages?.length > 0);
    const renderFormBody = () => {
        const { available: emailAvailable } = emailAvailability;
        const { showOfficeClosedBanner, showErrorBanner, errorBannerMessage,
            email, orderType, emailVerified } = landingPageState;
        const { name: memberName, officeCloseInfo, tpoEnabled } = member;

        if (!emailVerified && !tpoEnabled && orderType === ORDER_TYPE.THIRD_PARTY) {
            return (
                <TPODisabledContainer context='recessed' className='tpo-disabled'>
                    <Heading4>{memberName} has disabled third party ordering.</Heading4>
                    <TPODisabledMessage className='tpo-disabled-text'>
                        {memberName} requires that learners order their own credentials.
                    </TPODisabledMessage>
                    <Anchor href='https://www.parchment.com/order/on-behalf'
                            className='find-another-school'>Find another school</Anchor>
                </TPODisabledContainer>
            );
        } else if (!emailVerified) {
            return (
                <FormContainer context='recessed' className='landing-section'>
                    {
                        showErrorBanner && <FormContainer.Prefix className='error-banner-container'>
                            <Banner visible={showErrorBanner} title='error' context='error' dismissable={true}
                                    size='large' message={errorBannerMessage}
                                    onDismiss={() => setLandingPageState((prevState) => ({
                                        ...prevState,
                                        showErrorBanner: false
                                    }))} />
                        </FormContainer.Prefix>
                    }
                    {(member?.welcomeMessage || messagesAvailable) && renderWelcomeMessage()}
                    {
                        isOfficeClosed(officeCloseInfo.startTime, officeCloseInfo.endTime) &&
                        <StyledBanner visible={showOfficeClosedBanner}
                                      className='office-closure-banner'
                                      title='Office Closed'
                                      message={formatMessage(officeCloseInfo.message)}
                                      context='warning'
                                      dismissable={true}
                                      onDismiss={() => setLandingPageState((prevState) => ({
                                          ...prevState,
                                          showOfficeClosedBanner: false
                                      }))} />
                    }
                    <LandingForm
                        isHighSchool={member.memberType === 'HS'}
                        email={email}
                        isSubmittingForm={formSubmitting}
                        onChange={value => setLandingPageState((prevState) => ({...prevState, email: value}))}
                        onSubmit={() => setLandingPageState((prevState) => ({...prevState, isSubmittingForm: true}))}
                    />
                </FormContainer>
            );
        }

        if (emailAvailable && orderType === ORDER_TYPE.LEARNER) {
            return (
                <RegistrationView email={email}
                                  member={member}
                                  ssoInfo={ssoInfo}
                                  registrationTokenRecord={registrationTokenRecord}
                                  memberServiceMessages={memberServiceMessages}
                                  resetEmail={() => resetEmail()} />
            );
        } else if (emailAvailable && orderType === ORDER_TYPE.THIRD_PARTY) {
            return (
                <ThirdPartyConfirmationPage email={email}
                                            member={member}
                                            resetEmail={(orderType) => resetEmail(orderType)} />
            );
        } else if (!emailAvailable) {
            return (
                <LoginView email={email}
                           member={member}
                           resetEmail={(orderType) => resetEmail(orderType)}
                           orderType={orderType} />
            );
        }
    };
    const hasSSOData = ssoInfo && Object.keys(ssoInfo).length > 0;
    return (
        <BaseLayout titleText={displayFormTitle()}>
            { !hasSSOData &&
                <FormContainer context='ghost'
                               bodyType={FormContainer.BasicBody}
                               className='order-type-section'>
                    <OrderTypeOptionSection>
                        <OrderTypeOption name='order-type-learner'
                                         type='radio'
                                         value={ ORDER_TYPE.LEARNER }
                                         description='Ordering your own credentials or academic records'
                                         icon={<IndividualIcon />}
                                         onChange={() => setLandingPageState((prevState) => ({
                                             ...prevState,
                                             orderType: ORDER_TYPE.LEARNER
                                         }))}
                                         checked={landingPageState.orderType === ORDER_TYPE.LEARNER} />
                        {!registrationTokenRecord?.id &&
                            <>
                                <StyledDivider orientation='vertical'>or</StyledDivider>
                                <OrderTypeOption name='order-type-third-party'
                                    type='radio'
                                    value={ ORDER_TYPE.THIRD_PARTY }
                                    description='Ordering on behalf of someone else'
                                    icon={<ThirdPartyIcon />}
                                    onChange={() => setLandingPageState((prevState) => ({
                                        ...prevState,
                                        orderType: ORDER_TYPE.THIRD_PARTY
                                    }))}
                                    checked={landingPageState.orderType === ORDER_TYPE.THIRD_PARTY}
                                />
                            </>
                        }
                    </OrderTypeOptionSection>
                    {messagesAvailable && renderPageBasedMessages([MessageContext.ALERT, MessageContext.NOTICE])}
                </FormContainer>
            }
            { renderFormBody() }
        </BaseLayout>
    );
}

const StyledContainer = styled(ShowMoreContainer)`
    margin-bottom: 20px;
`;
StyledContainer.displayName = 'StyledContainer';

const WelcomeMessageContainer = styled.div`
    margin-bottom: 40px;
    div:after {
      box-shadow: 0px -5px 10px 5px ${wildSand};
    }
`;

const OrderTypeOptionSection = styled.div`
    display: flex;
    flex-direction: row;
    min-height: 200px;
    justify-content: space-evenly;
    align-items: center;
    padding: 0 20px;
`;
OrderTypeOptionSection.displayName = 'OrderTypeOptionSection';

const OrderTypeOption = styled(OptionButton)`
    width: 240px;

    label > div {
        font-size: 16px;
        font-weight: 600;
    }
`;
OrderTypeOption.displayName = 'OrderTypeOption';

const StyledDivider = styled(Divider)`
    text-transform: uppercase;
    font-weight: 600;
    color: ${mercury};
`;
StyledDivider.displayName = 'StyledDivider';

const Heading5 = styled(Heading5Raw)`
    text-transform: uppercase;
`;
Heading5.displayName = 'Heading5';

const MessageDiv = styled.div`
    &:not(:first-child) {
        margin-top: 10px;
    }
`;
MessageDiv.displayName = 'MessageDiv';

const StyledBanner = styled(Banner)`
    margin-bottom: 40px;
`;
StyledBanner.displayName = 'StyledBanner';

const TPODisabledContainer = styled(FormContainer)`
    text-align: center;
`;
TPODisabledContainer.displayName = 'TPODisabledContainer';

const TPODisabledMessage = styled.div`
    font-size: 16px;
    margin: 20px 0 10px 0;
`;
TPODisabledMessage.displayName = 'TPODisabledMessage';
